import React from 'react'
import { ThemeContext } from './../../theme/DarkMode';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import { guideList1, guideList2 } from '../../data';
import { DropDownGuideItems } from './DropDown';
import { useTranslation } from 'react-i18next';

export const Guide = () => {
  const { theme } = React.useContext(ThemeContext);
  const { i18n } = useTranslation();

  const getTitle = (item) => {
    const lang = i18n.language.toLowerCase();
    return item.title[lang] || item.title.en;
  }

  const isRTL = i18n.language.toLowerCase() === 'fa';

  const translations = {
    user: {
      en: "User",
      fa: "کاربر"
    },
    userDesc: {
      en: "A user who has registered in Ahil and is a consumer or buyer of the platform's applications, content, and views. A user in Ahil can simultaneously be a creator of their applications and views, a regular user of others' applications and views, and a manager of others' applications.",
      fa: "کاربری که در آهیل ثبت‌نام کرده و مصرف‌کننده یا خریدار برنامه‌ها، محتوا و نمایش‌های پلتفرم است. یک کاربر در آهیل می‌تواند همزمان خالق برنامه‌ها و نمایش‌های خود، کاربر عادی برنامه‌ها و نمایش‌های دیگران و مدیر برنامه‌های دیگران باشد."
    },
    userCapabilities: {
      en: "Regular user capabilities:",
      fa: "قابلیت‌های کاربر عادی:"
    },
    capabilities: {
      en: [
        "Registration",
        "Login",
        "Edit personal information",
        "Account verification",
        "Personalize the application (application settings)",
        "View personal calendar",
        "Manage personal views",
        "Create and edit personal views",
        "Manage the sale of personal views",
        "View purchase statistics",
        "View sales statistics",
        "Search public and protected content of others",
        "Join others' personal programs and views",
        "Change the status of tasks assigned to themselves within others' programs",
        "Buy others' personal programs and views",
        "Purchase sections within others' programs (after joining the program)"
      ],
      fa: [
        "ثبت‌نام",
        "ورود",
        "ویرایش اطلاعات شخصی",
        "تایید حساب کاربری",
        "شخصی‌سازی برنامه (تنظیمات برنامه)",
        "مشاهده تقویم شخصی",
        "مدیریت نمایش‌های شخصی",
        "ایجاد و ویرایش نمایش‌های شخصی",
        "مدیریت فروش نمایش‌های شخصی",
        "مشاهده آمار خریدها",
        "مشاهده آمار فروش",
        "جستجو در محتوای عمومی و محافظت شده دیگران",
        "عضویت در برنامه‌ها و نمایش‌های شخصی دیگران",
        "تغییر وضعیت وظایف محول شده به خود در برنامه‌های دیگران",
        "خرید برنامه‌ها و نمایش‌های شخصی دیگران",
        "خرید بخش‌های برنامه‌های دیگران (پس از عضویت در برنامه)"
      ]
    },
    owner: {
      en: "Owner",
      fa: "مالک"
    },
    ownerDesc: {
      en: "The owner of applications, sections, content, and views within them. Has all the capabilities of a user in addition to:",
      fa: "مالک برنامه‌ها، بخش‌ها، محتوا و نمایش‌های موجود در آنها. علاوه بر تمام قابلیت‌های کاربر، دارای موارد زیر است:"
    },
    ownerCapabilities: {
      en: [
        "Create and edit application structure (public, protected, private)",
        "Manage application content",
        "Create and edit sections",
        "Manage application users (add user, remove user, add manager, remove from management)",
        "View complete application statistics",
        "View complete application calendar",
        "View user statistics",
        "Change and approve the status of content within applications"
      ],
      fa: [
        "ایجاد و ویرایش ساختار برنامه (عمومی، محافظت شده، خصوصی)",
        "مدیریت محتوای برنامه",
        "ایجاد و ویرایش بخش‌ها",
        "مدیریت کاربران برنامه (افزودن کاربر، حذف کاربر، افزودن مدیر، حذف از مدیریت)",
        "مشاهده آمار کامل برنامه",
        "مشاهده تقویم کامل برنامه",
        "مشاهده آمار کاربران",
        "تغییر و تایید وضعیت محتوا در برنامه‌ها"
      ]
    },
    manager: {
      en: "Manager",
      fa: "مدیر"
    },
    managerDesc: {
      en: "Has all the capabilities of a user. Managed by the owner in the application and has the capabilities of the owner for complete application management. In the current version of Ahil, the manager has full access to the applications in which they are a manager.",
      fa: "دارای تمام قابلیت‌های یک کاربر است. توسط مالک در برنامه مدیریت می‌شود و دارای قابلیت‌های مالک برای مدیریت کامل برنامه است. در نسخه فعلی آهیل، مدیر دسترسی کامل به برنامه‌هایی که در آنها مدیر است را دارد."
    },
    program: {
      en: "Program",
      fa: "برنامه"
    },
    programDesc: {
      en: "Programs are the foundation of Ahil. Users within Ahil can create programs for themselves and others in a planning format and manage their daily lives and those of others within them. Users can simultaneously be owners, managers, and users of their own and others' programs. Programs can have sections. Sections are categories of program content. There are no sections outside of programs.",
      fa: "برنامه‌ها پایه و اساس آهیل هستند. کاربران در آهیل می‌توانند برنامه‌هایی را برای خود و دیگران به صورت برنامه‌ریزی شده ایجاد کنند و زندگی روزمره خود و دیگران را در آنها مدیریت کنند. کاربران می‌توانند همزمان مالک، مدیر و کاربر برنامه‌های خود و دیگران باشند. برنامه‌ها می‌توانند بخش‌هایی داشته باشند. بخش‌ها دسته‌بندی‌های محتوای برنامه هستند. هیچ بخشی خارج از برنامه‌ها وجود ندارد."
    },
    programViews: {
      en: "Programs have views. The views of the program are content that is not placed in any section of the program and is displayed on the main page of the program below the list of sections.",
      fa: "برنامه‌ها دارای نمایش هستند. نمایش‌های برنامه محتوایی هستند که در هیچ بخشی از برنامه قرار نمی‌گیرند و در صفحه اصلی برنامه زیر لیست بخش‌ها نمایش داده می‌شوند."
    },
    programJoin: {
      en: "To join the content of any program, one must first join the program.",
      fa: "برای عضویت در محتوای هر برنامه، ابتدا باید در برنامه عضو شوید."
    },
    section: {
      en: "Section",
      fa: "بخش"
    },
    sectionDesc: {
      en: "Sections are categories of content within the program. Within the program, various content can be categorized based on the subject or any classification required by the owner or managers within sections. Sections can have prices and access permissions. Membership in sections can also be set up (membership with approval). This means that membership requests in the section by users must be approved by the manager.",
      fa: "بخش‌ها دسته‌بندی‌های محتوا در برنامه هستند. در برنامه، محتوای متنوع می‌تواند بر اساس موضوع یا هر دسته‌بندی دیگری که توسط مالک یا مدیران در بخش‌ها مورد نیاز است، دسته‌بندی شود. بخش‌ها می‌توانند قیمت و دسترسی‌های مجاز داشته باشند. عضویت در بخش‌ها نیز می‌تواند تنظیم شود (عضویت با تایید). این بدان معنی است که درخواست‌های عضویت در بخش توسط کاربران باید توسط مدیر تایید شود."
    },
    view: {
      en: "View",
      fa: "نمایش"
    },
    viewDesc: {
      en: "Views are a type of content that is located in the views section of Ahil. There are two types of views in Ahil:",
      fa: "نمایش‌ها نوعی محتوا هستند که در بخش نمایش‌های آهیل قرار دارند. دو نوع نمایش در آهیل وجود دارد:"
    },
    personalView: {
      en: "User's personal view: A content created by the user outside the program and belongs to their account, displayed on their account page. This view can simultaneously be a task and an event. It can be either for sale or free. A view can be private, public, or protected. It can be a product for sale or motivational content.",
      fa: "نمایش شخصی کاربر: محتوایی که توسط کاربر خارج از برنامه ایجاد شده و متعلق به حساب کاربری اوست، در صفحه حساب کاربری نمایش داده می‌شود. این نمایش می‌تواند همزمان یک وظیفه و یک رویداد باشد. می‌تواند برای فروش یا رایگان باشد. یک نمایش می‌تواند خصوصی، عمومی یا محافظت شده باشد. می‌تواند یک محصول برای فروش یا محتوای انگیزشی باشد."
    },
    viewNote: {
      en: "Note: Public and protected views of the user will be visible to other users in their account, but private views are hidden from others; however, the owner of the view can add users to them.",
      fa: "نکته: نمایش‌های عمومی و محافظت شده کاربر برای سایر کاربران در حساب کاربری آنها قابل مشاهده خواهد بود، اما نمایش‌های خصوصی از دیگران پنهان هستند؛ با این حال، مالک نمایش می‌تواند کاربران را به آنها اضافه کند."
    },
    programView: {
      en: "Program view: A content created within the program but does not belong to a section and is displayed in the view section on the program page. Although a program view has all the capabilities of a personal view, it is usually used to introduce the program.",
      fa: "نمایش برنامه: محتوایی که در برنامه ایجاد شده اما به هیچ بخشی تعلق ندارد و در بخش نمایش در صفحه برنامه نمایش داده می‌شود. اگرچه یک نمایش برنامه دارای تمام قابلیت‌های نمایش شخصی است، اما معمولاً برای معرفی برنامه استفاده می‌شود."
    },
    content: {
      en: "Content",
      fa: "محتوا"
    },
    contentDesc: {
      en: "Contents are the intelligent contents of Ahil. For the first time in Ahil, contents with the capability of holding multiple diverse media are presented. To manage and plan for themselves and others, we can create various contents inside or outside the created programs.",
      fa: "محتواها، محتوای هوشمند آهیل هستند. برای اولین بار در آهیل، محتواهایی با قابلیت نگهداری چندین رسانه متنوع ارائه شده‌اند. برای مدیریت و برنامه‌ریزی برای خود و دیگران، می‌توانیم محتوای متنوعی را در داخل یا خارج از برنامه‌های ایجاد شده ایجاد کنیم."
    },
    notification: {
      en: "Notification",
      fa: "اعلان"
    },
    notificationDesc: {
      en: "Notifications are the message-based communication structure between Ahil users. Ahil notifications include: sale, membership request, creating new content in the program, performing tasks by program members. The notification system of Ahil is being updated by the Ahil development team. Content notifications are sent to all program users.",
      fa: "اعلان‌ها ساختار ارتباطی مبتنی بر پیام بین کاربران آهیل هستند. اعلان‌های آهیل شامل: فروش، درخواست عضویت، ایجاد محتوای جدید در برنامه، انجام وظایف توسط اعضای برنامه. سیستم اعلان آهیل توسط تیم توسعه آهیل در حال به‌روزرسانی است. اعلان‌های محتوا به تمام کاربران برنامه ارسال می‌شود."
    },
    notificationSettings: {
      en: "Activating and deactivating Ahil notifications is located in the settings page.",
      fa: "فعال‌سازی و غیرفعال‌سازی اعلان‌های آهیل در صفحه تنظیمات قرار دارد."
    },
    accessPermission: {
      en: "Access Permission",
      fa: "دسترسی مجاز"
    },
    accessPermissionDesc: {
      en: "Access permission is a feature for managing users' access to personal views of programs, sections, and program views.",
      fa: "دسترسی مجاز یک ویژگی برای مدیریت دسترسی کاربران به نمایش‌های شخصی برنامه‌ها، بخش‌ها و نمایش‌های برنامه است."
    },
    accessToPersonalViews: {
      en: "Access to Personal Views",
      fa: "دسترسی به نمایش‌های شخصی"
    },
    accessToPersonalViewsDesc1: {
      en: "In user personal views, if content is created with public access permission, it will be searchable on the Ahil platform and other users can fully view this content.",
      fa: "در نمایش‌های شخصی کاربر، اگر محتوا با دسترسی عمومی ایجاد شود، در پلتفرم آهیل قابل جستجو خواهد بود و سایر کاربران می‌توانند این محتوا را به طور کامل مشاهده کنند."
    },
    accessToPersonalViewsDesc2: {
      en: "In user personal views, if content is created with protected access permission, it will be searchable on the Ahil platform. However, users will not have access to the media present in the protected content. Content with protected access usually has a price so that users can join them after purchase and see the media of that content. However, this condition is not mandatory.",
      fa: "در نمایش‌های شخصی کاربر، اگر محتوا با دسترسی محافظت شده ایجاد شود، در پلتفرم آهیل قابل جستجو خواهد بود. با این حال، کاربران به رسانه‌های موجود در محتوای محافظت شده دسترسی نخواهند داشت. محتوای با دسترسی محافظت شده معمولاً دارای قیمت است تا کاربران پس از خرید بتوانند به آن‌ها بپیوندند و رسانه‌های آن محتوا را مشاهده کنند. با این حال، این شرط اجباری نیست."
    },
    accessToPersonalViewsDesc3: {
      en: "In user personal views, if the access permission of the content is set to private, that content will not be found on the Ahil platform search page at all, and only the owner of the content can add others to it.",
      fa: "در نمایش‌های شخصی کاربر، اگر دسترسی محتوا به صورت خصوصی تنظیم شود، آن محتوا در صفحه جستجوی پلتفرم آهیل اصلاً پیدا نخواهد شد و فقط مالک محتوا می‌تواند دیگران را به آن اضافه کند."
    },
    accessToProgramContents: {
      en: "Access to Program Contents",
      fa: "دسترسی به محتوای برنامه"
    },
    accessToProgramContentsDesc1: {
      en: "Programs with public access permission are searchable on the Ahil platform search page. Public and protected views of public programs are searchable and visible in the program. However, the media of the protected view of these types of programs is not visible before registration in the program and within that view. If a private view is created in a public program, it will not be found in the program page for members of the program and in the platform search page. Private views will not be visible to any user until the owner or manager directly adds the user to that view. If a private section is created in a public program, that section will not be visible to users of that program and users outside the program in the search page until the owner or manager directly adds users to that section. All contents (public, private, and protected) within private and protected sections in programs will not be visible to program members before registering in the section. Also, these contents will not be viewable or searchable on the Ahil platform search page.",
      fa: "برنامه‌های با دسترسی عمومی در صفحه جستجوی پلتفرم آهیل قابل جستجو هستند. نمایش‌های عمومی و محافظت شده برنامه‌های عمومی در برنامه قابل جستجو و قابل مشاهده هستند. با این حال، رسانه‌های نمایش محافظت شده این نوع برنامه‌ها قبل از ثبت‌نام در برنامه و در آن نمایش قابل مشاهده نیست. اگر یک نمایش خصوصی در یک برنامه عمومی ایجاد شود، در صفحه برنامه برای اعضای برنامه و در صفحه جستجوی پلتفرم پیدا نخواهد شد. نمایش‌های خصوصی تا زمانی که مالک یا مدیر مستقیماً کاربر را به آن نمایش اضافه نکند، برای هیچ کاربری قابل مشاهده نخواهد بود. اگر یک بخش خصوصی در یک برنامه عمومی ایجاد شود، آن بخش تا زمانی که مالک یا مدیر مستقیماً کاربران را به آن بخش اضافه نکند، برای کاربران آن برنامه و کاربران خارج از برنامه در صفحه جستجو قابل مشاهده نخواهد بود. تمام محتواها (عمومی، خصوصی و محافظت شده) در بخش‌های خصوصی و محافظت شده در برنامه‌ها قبل از ثبت‌نام در بخش برای اعضای برنامه قابل مشاهده نخواهند بود. همچنین، این محتواها در صفحه جستجوی پلتفرم آهیل قابل مشاهده یا جستجو نخواهند بود."
    },
    accessToProgramContentsDesc2: {
      en: "If a program is created with protected access permission, that program will be searchable on the Ahil platform search page. However, the sections of the protected program with any type of access permission (public, private, and protected) will not be visible before registration in the program. Contents within the sections of the protected program will also not be searchable on the Ahil platform search page with any type of access permission (public, private, and protected). Private views of the protected program will not be visible or searchable by users in the content section of the Ahil platform search page. The protected view of the protected program will be visible and searchable on the Ahil platform search page; however, to join it, one must first register in the program. However, before joining the protected view of this program, the contents of the protected view will not be visible. The public view of the protected program will be visible and searchable on the Ahil platform search page; its media will be viewable; however, to join this view, one must also register in the program first.",
      en: "In program sections, if the section is public, all users who have joined the program can view the public content of the section. If the section is protected, users who have joined the program can view the public content of the section, but they need to purchase the section to view the protected content. If the section is private, only users who have been added to the section by the owner can view the content.",
      fa: "در بخش‌های برنامه، اگر بخش عمومی باشد، تمام کاربرانی که به برنامه پیوسته‌اند می‌توانند محتوای عمومی بخش را مشاهده کنند. اگر بخش محافظت شده باشد، کاربرانی که به برنامه پیوسته‌اند می‌توانند محتوای عمومی بخش را مشاهده کنند، اما برای مشاهده محتوای محافظت شده نیاز به خرید بخش دارند. اگر بخش خصوصی باشد، فقط کاربرانی که توسط مالک به بخش اضافه شده‌اند می‌توانند محتوا را مشاهده کنند."
    },
    accessToProgramContentsDesc3: {
      en: "In program views, if the view is public, all users who have joined the program can view the public content of the view. If the view is protected, users who have joined the program can view the public content of the view, but they need to purchase the view to view the protected content. If the view is private, only users who have been added to the view by the owner can view the content.",
      fa: "در نمایش‌های برنامه، اگر نمایش عمومی باشد، تمام کاربرانی که به برنامه پیوسته‌اند می‌توانند محتوای عمومی نمایش را مشاهده کنند. اگر نمایش محافظت شده باشد، کاربرانی که به برنامه پیوسته‌اند می‌توانند محتوای عمومی نمایش را مشاهده کنند، اما برای مشاهده محتوای محافظت شده نیاز به خرید نمایش دارند. اگر نمایش خصوصی باشد، فقط کاربرانی که توسط مالک به نمایش اضافه شده‌اند می‌توانند محتوا را مشاهده کنند."
    },
    membership: {
      en: "Membership",
      fa: "عضویت"
    },
    membershipDesc: {
      en: "Membership is a feature for joining programs, sections, and views. When a user joins a program, section, or view, they become a member of that program, section, or view.",
      fa: "عضویت یک ویژگی برای پیوستن به برنامه‌ها، بخش‌ها و نمایش‌ها است. وقتی کاربری به یک برنامه، بخش یا نمایش می‌پیوندد، به عضویت آن برنامه، بخش یا نمایش در می‌آید."
    },
    membershipCapabilities: {
      en: [
        "Joining programs, sections, and views",
        "Accessing content based on membership level",
        "Participating in discussions and comments",
        "Receiving notifications about updates"
      ],
      fa: [
        "پیوستن به برنامه‌ها، بخش‌ها و نمایش‌ها",
        "دسترسی به محتوا بر اساس سطح عضویت",
        "شرکت در بحث‌ها و نظرات",
        "دریافت اعلان‌ها درباره به‌روزرسانی‌ها"
      ]
    },
    membershipTypes: {
      en: "Membership Types",
      fa: "انواع عضویت"
    },
    membershipTypesDesc: {
      en: "There are different types of membership in Ahil:",
      fa: "انواع مختلف عضویت در آهیل وجود دارد:"
    },
    freeMembership: {
      en: "Free Membership",
      fa: "عضویت رایگان"
    },
    freeMembershipDesc: {
      en: "Free membership allows users to join public programs, sections, and views without any cost. Users can access public content and participate in discussions.",
      fa: "عضویت رایگان به کاربران اجازه می‌دهد بدون هیچ هزینه‌ای به برنامه‌ها، بخش‌ها و نمایش‌های عمومی بپیوندند. کاربران می‌توانند به محتوای عمومی دسترسی داشته باشند و در بحث‌ها شرکت کنند."
    },
    paidMembership: {
      en: "Paid Membership",
      fa: "عضویت پولی"
    },
    paidMembershipDesc: {
      en: "Paid membership requires users to purchase access to specific programs, sections, or views. This type of membership provides access to protected content and additional features.",
      fa: "عضویت پولی نیازمند خرید دسترسی به برنامه‌ها، بخش‌ها یا نمایش‌های خاص است. این نوع عضویت دسترسی به محتوای محافظت شده و ویژگی‌های اضافی را فراهم می‌کند."
    },
    premiumMembership: {
      en: "Premium Membership",
      fa: "عضویت ویژه"
    },
    premiumMembershipDesc: {
      en: "Premium membership offers exclusive access to private content and advanced features. This type of membership is typically offered for a higher price and includes additional benefits.",
      fa: "عضویت ویژه دسترسی انحصاری به محتوای خصوصی و ویژگی‌های پیشرفته را ارائه می‌دهد. این نوع عضویت معمولاً با قیمت بالاتری ارائه می‌شود و شامل مزایای اضافی است."
    },
    membershipBenefits: {
      en: "Membership Benefits",
      fa: "مزایای عضویت"
    },
    membershipBenefitsDesc: {
      en: "Membership in Ahil provides various benefits to users:",
      fa: "عضویت در آهیل مزایای مختلفی را برای کاربران فراهم می‌کند:"
    },
    contentAccess: {
      en: "Content Access",
      fa: "دسترسی به محتوا"
    },
    contentAccessDesc: {
      en: "Members can access content based on their membership level and the access permissions of the content.",
      fa: "اعضا می‌توانند بر اساس سطح عضویت خود و دسترسی‌های مجاز محتوا به آن دسترسی داشته باشند."
    },
    communityInteraction: {
      en: "Community Interaction",
      fa: "تعامل با جامعه"
    },
    communityInteractionDesc: {
      en: "Members can participate in discussions, leave comments, and interact with other members of the program, section, or view.",
      fa: "اعضا می‌توانند در بحث‌ها شرکت کنند، نظر بدهند و با سایر اعضای برنامه، بخش یا نمایش تعامل داشته باشند."
    },
    notifications: {
      en: "Notifications",
      fa: "اعلان‌ها"
    },
    notificationsDesc: {
      en: "Members receive notifications about updates, new content, and important announcements related to their memberships.",
      fa: "اعضا اعلان‌هایی درباره به‌روزرسانی‌ها، محتوای جدید و اعلان‌های مهم مربوط به عضویت‌های خود دریافت می‌کنند."
    },
    membershipRestrictions: {
      en: "Membership Restrictions",
      fa: "محدودیت‌های عضویت"
    },
    membershipRestrictionsDesc: {
      en: "There are certain restrictions that apply to membership:",
      fa: "محدودیت‌های خاصی برای عضویت وجود دارد:"
    },
    contentLimitations: {
      en: "Content Limitations",
      fa: "محدودیت‌های محتوا"
    },
    contentLimitationsDesc: {
      en: "Access to content is limited based on membership type and level. Some content may only be available to paid or premium members.",
      fa: "دسترسی به محتوا بر اساس نوع و سطح عضویت محدود شده است. برخی محتواها ممکن است فقط برای اعضای پولی یا ویژه در دسترس باشند."
    },
    timeRestrictions: {
      en: "Time Restrictions",
      fa: "محدودیت‌های زمانی"
    },
    timeRestrictionsDesc: {
      en: "Some memberships may have time limitations for accessing certain content or features.",
      fa: "برخی عضویت‌ها ممکن است محدودیت‌های زمانی برای دسترسی به محتوای خاص یا ویژگی‌ها داشته باشند."
    },
    usageRestrictions: {
      en: "Usage Restrictions",
      fa: "محدودیت‌های استفاده"
    },
    usageRestrictionsDesc: {
      en: "Members must comply with the platform's terms of service and usage guidelines. Violation of these rules may result in membership termination.",
      fa: "اعضا باید با شرایط خدمات و دستورالعمل‌های استفاده از پلتفرم comply کنند. نقض این قوانین ممکن است منجر به خاتمه عضویت شود."
    },
    membershipManagement: {
      en: "Membership Management",
      fa: "مدیریت عضویت"
    },
    membershipManagementDesc: {
      en: "Members can manage their memberships through their account settings:",
      fa: "اعضا می‌توانند عضویت‌های خود را از طریق تنظیمات حساب کاربری خود مدیریت کنند:"
    },
    viewMemberships: {
      en: "View Memberships",
      fa: "مشاهده عضویت‌ها"
    },
    viewMembershipsDesc: {
      en: "Members can view all their current memberships, including programs, sections, and views they have joined.",
      fa: "اعضا می‌توانند تمام عضویت‌های فعلی خود، از جمله برنامه‌ها، بخش‌ها و نمایش‌هایی که به آن‌ها پیوسته‌اند را مشاهده کنند."
    },
    cancelMembership: {
      en: "Cancel Membership",
      fa: "لغو عضویت"
    },
    cancelMembershipDesc: {
      en: "Members can cancel their membership to any program, section, or view at any time. Cancellation will take effect at the end of the current billing period.",
      fa: "اعضا می‌توانند در هر زمان عضویت خود را در هر برنامه، بخش یا نمایش لغو کنند. لغو عضویت در پایان دوره صورتحساب فعلی生效 خواهد شد."
    },
    membershipRenewal: {
      en: "Membership Renewal",
      fa: "تجدید عضویت"
    },
    membershipRenewalDesc: {
      en: "Memberships can be renewed before they expire:",
      fa: "عضویت‌ها می‌توانند قبل از انقضا تجدید شوند:"
    },
    automaticRenewal: {
      en: "Automatic Renewal",
      fa: "تجدید خودکار"
    },
    automaticRenewalDesc: {
      en: "Members can enable automatic renewal for their memberships. This will automatically extend their membership when it expires.",
      fa: "اعضا می‌توانند تجدید خودکار را برای عضویت‌های خود فعال کنند. این کار عضویت آن‌ها را به طور خودکار در زمان انقضا تمدید خواهد کرد."
    },
    manualRenewal: {
      en: "Manual Renewal",
      fa: "تجدید دستی"
    },
    manualRenewalDesc: {
      en: "Members can manually renew their memberships before they expire. They will receive a notification when their membership is about to expire.",
      fa: "اعضا می‌توانند عضویت‌های خود را قبل از انقضا به صورت دستی تجدید کنند. آن‌ها یک اعلان دریافت خواهند کرد که عضویت آن‌ها در حال انقضا است."
    },
    renewalReminders: {
      en: "Renewal Reminders",
      fa: "یادآوری‌های تجدید"
    },
    renewalRemindersDesc: {
      en: "Members will receive reminders about their membership expiration and renewal options.",
      fa: "اعضا یادآوری‌هایی درباره انقضای عضویت و گزینه‌های تجدید دریافت خواهند کرد."
    },
    exit: {
      en: "Exit",
      fa: "خروج"
    },
    exitDesc: {
      en: "To log out of the user account, you can use this option.",
      fa: "برای خروج از حساب کاربری، می‌توانید از این گزینه استفاده کنید."
    },
    addIcon: {
      en: "Add Icon",
      fa: "آیکون افزودن"
    },
    addIconDesc: {
      en: "The plus icon at the top of the user account page allows the user to create content and programs.",
      fa: "آیکون به علاوه در بالای صفحه حساب کاربری به کاربر امکان ایجاد محتوا و برنامه را می‌دهد."
    },
    createContent: {
      en: "Create Content",
      fa: "ایجاد محتوا"
    },
    createContentDesc: {
      en: "On this page, you can create personal view contents and contents within various programs. A content can be a task, event, or both simultaneously based on the user's needs. When a content is both a task and an event, it follows the main rules of events in Ahil's programs and calendars. This simultaneity is only used to add status to the event. The 'more' button at the bottom of the content creation page shows all the customizable features for the content.",
      fa: "در این صفحه می‌توانید محتوای نمایش شخصی و محتواهای درون برنامه‌های مختلف را ایجاد کنید. یک محتوا می‌تواند بر اساس نیاز کاربر همزمان یک وظیفه، رویداد یا هر دو باشد. وقتی یک محتوا هم وظیفه و هم رویداد است، از قوانین اصلی رویدادها در برنامه‌ها و تقویم‌های آهیل پیروی می‌کند. این همزمانی فقط برای افزودن وضعیت به رویداد استفاده می‌شود. دکمه 'بیشتر' در پایین صفحه ایجاد محتوا تمام ویژگی‌های قابل تنظیم برای محتوا را نشان می‌دهد."
    },
    createContentNote: {
      en: "Upon joining a program, users automatically join public contents with dates, except for events. Also, this automatic membership occurs in the contents of the program sections for the members of that section.",
      fa: "پس از عضویت در یک برنامه، کاربران به طور خودکار به محتوای عمومی دارای تاریخ می‌پیوندند، به جز رویدادها. همچنین، این عضویت خودکار در محتوای بخش‌های برنامه برای اعضای آن بخش رخ می‌دهد."
    },
    createContentImportant: {
      en: "Important note: The title, description, and media of each content are optional. The user only needs to provide at least one of the characteristics: title, description, or media. However, contents without titles and descriptions will be presented as suggestions to the user on the main search page; but for textual search, contents must have either a title or a description. The following characteristics can be set when creating content:",
      fa: "نکته مهم: عنوان، توضیحات و رسانه هر محتوا اختیاری است. کاربر فقط نیاز دارد حداقل یکی از ویژگی‌های زیر را ارائه دهد: عنوان، توضیحات یا رسانه. با این حال، محتواهای بدون عنوان و توضیحات به عنوان پیشنهادات به کاربر در صفحه جستجوی اصلی ارائه می‌شوند؛ اما برای جستجوی متنی، محتواها باید دارای عنوان یا توضیحات باشند. ویژگی‌های زیر را می‌توان هنگام ایجاد محتوا تنظیم کرد:"
    },
    programFeature: {
      en: "Program Feature",
      fa: "ویژگی برنامه"
    },
    programFeatureDesc: {
      en: "On this page, you can create programs and set their features. A program can be a task, event, or both simultaneously based on the user's needs. When a program is both a task and an event, it follows the main rules of events in Ahil's programs and calendars. This simultaneity is only used to add status to the event. The 'more' button at the bottom of the program creation page shows all the customizable features for the program.",
      fa: "در این صفحه می‌توانید برنامه‌ها را ایجاد کرده و ویژگی‌های آن‌ها را تنظیم کنید. یک برنامه می‌تواند بر اساس نیاز کاربر همزمان یک وظیفه، رویداد یا هر دو باشد. وقتی یک برنامه هم وظیفه و هم رویداد است، از قوانین اصلی رویدادها در برنامه‌ها و تقویم‌های آهیل پیروی می‌کند. این همزمانی فقط برای افزودن وضعیت به رویداد استفاده می‌شود. دکمه 'بیشتر' در پایین صفحه ایجاد برنامه تمام ویژگی‌های قابل تنظیم برای برنامه را نشان می‌دهد."
    },
    programFeatureNote: {
      en: "Important note: The title, description, and media of each program are optional. The user only needs to provide at least one of the characteristics: title, description, or media. However, programs without titles and descriptions will be presented as suggestions to the user on the main search page; but for textual search, programs must have either a title or a description. The following characteristics can be set when creating a program:",
      fa: "نکته مهم: عنوان، توضیحات و رسانه هر برنامه اختیاری است. کاربر فقط نیاز دارد حداقل یکی از ویژگی‌های زیر را ارائه دهد: عنوان، توضیحات یا رسانه. با این حال، برنامه‌های بدون عنوان و توضیحات به عنوان پیشنهادات به کاربر در صفحه جستجوی اصلی ارائه می‌شوند؛ اما برای جستجوی متنی، برنامه‌ها باید دارای عنوان یا توضیحات باشند. ویژگی‌های زیر را می‌توان هنگام ایجاد برنامه تنظیم کرد:"
    },
    programAccess: {
      en: "Program Access",
      fa: "دسترسی برنامه"
    },
    programAccessDesc: {
      en: "The access level of a program determines who can see and join it. There are three levels of access: public, protected, and private. Public programs are visible to everyone and can be joined by anyone. Protected programs are visible to everyone but can only be joined by invitation. Private programs are only visible to members and can only be joined by invitation.",
      fa: "سطح دسترسی یک برنامه تعیین می‌کند که چه کسانی می‌توانند آن را ببینند و به آن بپیوندند. سه سطح دسترسی وجود دارد: عمومی، محافظت شده و خصوصی. برنامه‌های عمومی برای همه قابل مشاهده هستند و هر کسی می‌تواند به آن‌ها بپیوندد. برنامه‌های محافظت شده برای همه قابل مشاهده هستند اما فقط با دعوت می‌توان به آن‌ها پیوست. برنامه‌های خصوصی فقط برای اعضا قابل مشاهده هستند و فقط با دعوت می‌توان به آن‌ها پیوست."
    },
    programMembership: {
      en: "Program Membership",
      fa: "عضویت در برنامه"
    },
    programMembershipDesc: {
      en: "Program membership determines what type of users can join the program. There are various types of memberships available, such as free, paid, trial, lifetime, subscription, group, corporate, student, teacher, parent, child, senior, veteran, disabled, unemployed, and retired. The program owner can set the membership type and conditions for joining.",
      fa: "عضویت در برنامه تعیین می‌کند که چه نوع کاربرانی می‌توانند به برنامه بپیوندند. انواع مختلفی از عضویت‌ها موجود است، مانند رایگان، پولی، آزمایشی، مادام‌العمر، اشتراکی، گروهی، شرکتی، دانش‌آموزی، معلمی، والدین، کودک، سالمند، جانباز، معلول، بیکار و بازنشسته. صاحب برنامه می‌تواند نوع عضویت و شرایط پیوستن را تعیین کند."
    },
    sectionFeature: {
      en: "Section Feature",
      fa: "ویژگی بخش"
    },
    sectionFeatureDesc: {
      en: "This feature allows you to create sections within programs. Sections help organize content and provide a structured way to group related items together. Each section can have its own settings for access, membership, and content organization.",
      fa: "این ویژگی به شما امکان می‌دهد بخش‌هایی را درون برنامه‌ها ایجاد کنید. بخش‌ها به سازماندهی محتوا کمک کرده و روشی ساختاریافته برای گروه‌بندی موارد مرتبط فراهم می‌کنند. هر بخش می‌تواند تنظیمات خاص خود را برای دسترسی، عضویت و سازماندهی محتوا داشته باشد."
    },
    sectionAccess: {
      en: "Section Access",
      fa: "دسترسی بخش"
    },
    sectionAccessDesc: {
      en: "Similar to program access, sections can have different access levels: public, protected, and private. The access level of a section determines who can see and join it. Public sections are visible to all program members, protected sections require invitation, and private sections are only visible to section members.",
      fa: "مانند دسترسی برنامه، بخش‌ها می‌توانند سطوح دسترسی مختلفی داشته باشند: عمومی، محافظت شده و خصوصی. سطح دسترسی یک بخش تعیین می‌کند که چه کسانی می‌توانند آن را ببینند و به آن بپیوندند. بخش‌های عمومی برای همه اعضای برنامه قابل مشاهده هستند، بخش‌های محافظت شده نیاز به دعوت دارند، و بخش‌های خصوصی فقط برای اعضای بخش قابل مشاهده هستند."
    },
    sectionMembership: {
      en: "Section Membership",
      fa: "عضویت در بخش"
    },
    sectionMembershipDesc: {
      en: "Section membership works similarly to program membership, but at a more granular level. Members of a program can join sections based on their membership type and the section's settings. Section owners can manage members and set specific permissions for different types of members.",
      fa: "عضویت در بخش مشابه عضویت در برنامه عمل می‌کند، اما در سطح جزئی‌تر. اعضای یک برنامه می‌توانند بر اساس نوع عضویت خود و تنظیمات بخش، به بخش‌ها بپیوندند. صاحبان بخش می‌توانند اعضا را مدیریت کرده و مجوزهای خاصی را برای انواع مختلف اعضا تنظیم کنند."
    },
    sectionContent: {
      en: "Section Content",
      fa: "محتوی بخش"
    },
    sectionContentDesc: {
      en: "Sections can contain various types of content, including tasks, events, and other program-specific items. Content within a section inherits the section's access settings unless specifically overridden. Section owners can organize content using categories, tags, or custom fields.",
      fa: "بخش‌ها می‌توانند شامل انواع مختلفی از محتوا باشند، از جمله وظایف، رویدادها و سایر موارد خاص برنامه. محتوای درون یک بخش، تنظیمات دسترسی بخش را به ارث می‌برد مگر اینکه به طور خاص تغییر داده شود. صاحبان بخش می‌توانند محتوا را با استفاده از دسته‌بندی‌ها، برچسب‌ها یا فیلدهای سفارشی سازماندهی کنند."
    },
    titleFeature: {
      en: "Title Feature",
      fa: "ویژگی عنوان"
    },
    titleFeatureDesc: {
      en: "The title is a required field for content, programs, and sections. It should be descriptive and clearly indicate the purpose or subject of the item. Titles are used for search, navigation, and display purposes throughout the platform.",
      fa: "عنوان یک فیلد اجباری برای محتوا، برنامه‌ها و بخش‌ها است. باید توصیفی باشد و به وضوح هدف یا موضوع مورد نظر را نشان دهد. عناوین برای جستجو، پیمایش و نمایش در سراسر پلتفرم استفاده می‌شوند."
    },
    titleRules: {
      en: "Title Rules",
      fa: "قوانین عنوان"
    },
    titleRulesDesc: {
      en: "Titles should follow these guidelines:\n- Be clear and concise\n- Use appropriate language\n- Avoid special characters\n- Be unique within the same context\n- Not exceed maximum length limits",
      fa: "عناوین باید از این دستورالعمل‌ها پیروی کنند:\n- واضح و مختصر باشند\n- از زبان مناسب استفاده کنند\n- از کاراکترهای خاص پرهیز کنند\n- در همان زمینه منحصر به فرد باشند\n- از محدودیت‌های طول بیشتر نشوند"
    },
    titleSearch: {
      en: "Title Search",
      fa: "جستجوی عنوان"
    },
    titleSearchDesc: {
      en: "Titles are indexed for search functionality. Users can search for content, programs, and sections using their titles. The search algorithm considers partial matches and related terms to provide relevant results.",
      fa: "عناوین برای عملکرد جستجو نمایه‌سازی می‌شوند. کاربران می‌توانند با استفاده از عناوین، محتوا، برنامه‌ها و بخش‌ها را جستجو کنند. الگوریتم جستجو تطابق‌های جزئی و اصطلاحات مرتبط را در نظر می‌گیرد تا نتایج مرتبط را ارائه دهد."
    },
    descriptionFeature: {
      en: "Description Feature",
      fa: "ویژگی توضیحات"
    },
    descriptionFeatureDesc: {
      en: "The description field provides detailed information about content, programs, and sections. It helps users understand the purpose, requirements, and additional context of the item. Descriptions are used for search and display purposes.",
      fa: "فیلد توضیحات اطلاعات دقیق درباره محتوا، برنامه‌ها و بخش‌ها را ارائه می‌دهد. به کاربران کمک می‌کند تا هدف، الزامات و زمینه‌های اضافی مورد نظر را درک کنند. توضیحات برای جستجو و نمایش استفاده می‌شوند."
    },
    descriptionFormat: {
      en: "Description Format",
      fa: "فرمت توضیحات"
    },
    descriptionFormatDesc: {
      en: "Descriptions can include:\n- Text content\n- Links\n- Lists\n- Basic formatting\n- Media references\n- Important notes and warnings",
      fa: "توضیحات می‌توانند شامل موارد زیر باشند:\n- محتوای متنی\n- لینک‌ها\n- لیست‌ها\n- فرمت‌بندی پایه\n- ارجاعات رسانه‌ای\n- یادداشت‌ها و هشدارهای مهم"
    },
    descriptionSearch: {
      en: "Description Search",
      fa: "جستجوی توضیحات"
    },
    descriptionSearchDesc: {
      en: "Like titles, descriptions are indexed for search functionality. The search algorithm considers both title and description content to provide comprehensive results. Users can find items based on keywords and phrases within descriptions.",
      fa: "مانند عناوین، توضیحات برای عملکرد جستجو نمایه‌سازی می‌شوند. الگوریتم جستجو هم محتوای عنوان و هم توضیحات را در نظر می‌گیرد تا نتایج جامع ارائه دهد. کاربران می‌توانند موارد را بر اساس کلمات کلیدی و عبارات موجود در توضیحات پیدا کنند."
    },
    mediaFeature: {
      en: "Media Feature",
      fa: "ویژگی رسانه"
    },
    mediaFeatureDesc: {
      en: "The media feature allows users to add various types of media files to their content, programs, and sections. Supported media types include images, videos, documents, and other file formats. Media files enhance the visual appeal and information value of the content.",
      fa: "ویژگی رسانه به کاربران امکان می‌دهد انواع مختلف فایل‌های رسانه‌ای را به محتوا، برنامه‌ها و بخش‌های خود اضافه کنند. انواع رسانه‌های پشتیبانی شده شامل تصاویر، ویدیوها، اسناد و سایر فرمت‌های فایل است. فایل‌های رسانه‌ای جذابیت بصری و ارزش اطلاعاتی محتوا را افزایش می‌دهند."
    },
    mediaTypes: {
      en: "Media Types",
      fa: "انواع رسانه"
    },
    mediaTypesDesc: {
      en: "Supported media types include:\n- Images (JPG, PNG, GIF)\n- Videos (MP4, WebM)\n- Documents (PDF, DOC, DOCX)\n- Audio files (MP3, WAV)\n- Presentations (PPT, PPTX)\n- Spreadsheets (XLS, XLSX)",
      fa: "انواع رسانه‌های پشتیبانی شده شامل:\n- تصاویر (JPG، PNG، GIF)\n- ویدیوها (MP4، WebM)\n- اسناد (PDF، DOC، DOCX)\n- فایل‌های صوتی (MP3، WAV)\n- ارائه‌ها (PPT، PPTX)\n- صفحات گسترده (XLS، XLSX)"
    },
    mediaUpload: {
      en: "Media Upload",
      fa: "آپلود رسانه"
    },
    mediaUploadDesc: {
      en: "Users can upload media files through:\n- Drag and drop\n- File selection dialog\n- URL import\n- Cloud storage integration\nFiles are automatically processed and optimized for web display.",
      fa: "کاربران می‌توانند فایل‌های رسانه‌ای را از طریق:\n- کشیدن و رها کردن\n- دیالوگ انتخاب فایل\n- وارد کردن URL\n- یکپارچه‌سازی با ذخیره‌سازی ابری\nآپلود کنند. فایل‌ها به طور خودکار پردازش و بهینه‌سازی می‌شوند."
    },
    mediaDisplay: {
      en: "Media Display",
      fa: "نمایش رسانه"
    },
    mediaDisplayDesc: {
      en: "Media files are displayed in various ways depending on their type:\n- Images are shown in galleries or slideshows\n- Videos are played in embedded players\n- Documents are available for download or preview\n- Audio files can be played directly\nAll media is responsive and adapts to different screen sizes.",
      fa: "فایل‌های رسانه‌ای به روش‌های مختلف بسته به نوع آنها نمایش داده می‌شوند:\n- تصاویر در گالری‌ها یا نمایش اسلاید نمایش داده می‌شوند\n- ویدیوها در پخش‌کننده‌های تعبیه شده پخش می‌شوند\n- اسناد برای دانلود یا پیش‌نمایش در دسترس هستند\n- فایل‌های صوتی می‌توانند مستقیماً پخش شوند\nهمه رسانه‌ها واکنش‌گرا هستند و با اندازه‌های مختلف صفحه سازگار می‌شوند."
    },
    accessPermissionFeature: {
      en: "Access Permission Feature",
      fa: "ویژگی دسترسی"
    },
    accessPermissionFeatureDesc: {
      en: "The access permission feature controls who can view and interact with content, programs, and sections. It provides three levels of access: public, protected, and private. Each level has specific visibility and interaction rules.",
      fa: "ویژگی دسترسی کنترل می‌کند که چه کسانی می‌توانند محتوا، برنامه‌ها و بخش‌ها را مشاهده و با آنها تعامل داشته باشند. سه سطح دسترسی ارائه می‌دهد: عمومی، محافظت شده و خصوصی. هر سطح قوانین خاصی برای مشاهده و تعامل دارد."
    },
    publicAccess: {
      en: "Public Access",
      fa: "دسترسی عمومی"
    },
    publicAccessDesc: {
      en: "Public content is visible to everyone and can be accessed without restrictions. Anyone can view, search, and interact with public content. This is suitable for content that needs wide visibility and engagement.",
      fa: "محتوی عمومی برای همه قابل مشاهده است و می‌توان بدون محدودیت به آن دسترسی داشت. هر کسی می‌تواند محتوای عمومی را مشاهده، جستجو و با آن تعامل داشته باشد. این مناسب برای محتوایی است که نیاز به دید گسترده و تعامل دارد."
    },
    protectedAccess: {
      en: "Protected Access",
      fa: "دسترسی محافظت شده"
    },
    protectedAccessDesc: {
      en: "Protected content is visible to everyone but requires specific permissions for interaction. Users can see the content exists but need invitation or approval to join, comment, or participate. This is useful for content that needs controlled engagement.",
      fa: "محتوی محافظت شده برای همه قابل مشاهده است اما برای تعامل نیاز به مجوزهای خاص دارد. کاربران می‌توانند وجود محتوا را ببینند اما برای پیوستن، نظر دادن یا مشارکت نیاز به دعوت یا تأیید دارند. این برای محتوایی که نیاز به تعامل کنترل شده دارد مفید است."
    },
    privateAccess: {
      en: "Private Access",
      fa: "دسترسی خصوصی"
    },
    privateAccessDesc: {
      en: "Private content is only visible to members with specific permissions. It is not searchable or visible to non-members. This is ideal for sensitive or exclusive content that should only be accessible to a select group.",
      fa: "محتوی خصوصی فقط برای اعضایی با مجوزهای خاص قابل مشاهده است. برای غیر اعضا قابل جستجو یا مشاهده نیست. این برای محتوای حساس یا انحصاری که فقط باید برای گروه خاصی قابل دسترسی باشد ایده‌آل است."
    },
    accessManagement: {
      en: "Access Management",
      fa: "مدیریت دسترسی"
    },
    accessManagementDesc: {
      en: "Content owners can manage access permissions through:\n- Member invitations\n- Permission settings\n- Access level changes\n- Member removal\nAccess settings can be modified at any time to accommodate changing needs.",
      fa: "صاحبان محتوا می‌توانند مجوزهای دسترسی را از طریق:\n- دعوت اعضا\n- تنظیمات مجوز\n- تغییر سطح دسترسی\n- حذف اعضا\nمدیریت کنند. تنظیمات دسترسی را می‌توان در هر زمان برای تطبیق با نیازهای متغیر تغییر داد."
    },
    staticTimingFeature: {
      en: "Static Timing Feature",
      fa: "ویژگی زمان‌بندی ثابت"
    },
    staticTimingFeatureDesc: {
      en: "The static timing feature allows users to set specific dates and times for content, programs, and sections. This helps in organizing and scheduling items for better time management and coordination.",
      fa: "ویژگی زمان‌بندی ثابت به کاربران امکان می‌دهد تاریخ و زمان خاصی را برای محتوا، برنامه‌ها و بخش‌ها تنظیم کنند. این به سازماندهی و برنامه‌ریزی موارد برای مدیریت زمان و هماهنگی بهتر کمک می‌کند."
    },
    dateSetting: {
      en: "Date Setting",
      fa: "تنظیم تاریخ"
    },
    dateSettingDesc: {
      en: "Users can set:\n- Start date\n- End date\n- Duration\n- Recurring dates\n- Time zones\nDates can be set for one-time events or recurring schedules.",
      fa: "کاربران می‌توانند تنظیم کنند:\n- تاریخ شروع\n- تاریخ پایان\n- مدت زمان\n- تاریخ‌های تکرار شونده\n- منطقه زمانی\nتاریخ‌ها را می‌توان برای رویدادهای یکبار یا برنامه‌های تکرار شونده تنظیم کرد."
    },
    timeManagement: {
      en: "Time Management",
      fa: "مدیریت زمان"
    },
    timeManagementDesc: {
      en: "Time management features include:\n- Calendar integration\n- Reminder settings\n- Deadline tracking\n- Schedule conflicts detection\n- Time zone adjustments\nThese features help users stay organized and meet deadlines.",
      fa: "ویژگی‌های مدیریت زمان شامل:\n- یکپارچه‌سازی تقویم\n- تنظیمات یادآوری\n- پیگیری مهلت‌ها\n- تشخیص تداخل برنامه\n- تنظیمات منطقه زمانی\nاین ویژگی‌ها به کاربران کمک می‌کنند تا سازماندهی شده باشند و مهلت‌ها را رعایت کنند."
    },
    schedulingOptions: {
      en: "Scheduling Options",
      fa: "گزینه‌های برنامه‌ریزی"
    },
    schedulingOptionsDesc: {
      en: "Various scheduling options are available:\n- One-time events\n- Daily schedules\n- Weekly patterns\n- Monthly recurring\n- Custom intervals\n- Flexible timing\nUsers can choose the most suitable option for their needs.",
      fa: "گزینه‌های مختلف برنامه‌ریزی در دسترس هستند:\n- رویدادهای یکبار\n- برنامه‌های روزانه\n- الگوهای هفتگی\n- تکرار ماهانه\n- فاصله‌های سفارشی\n- زمان‌بندی انعطاف‌پذیر\nکاربران می‌توانند گزینه‌ای که بیشترین تناسب را با نیازهای آنها دارد انتخاب کنند."
    },
    floatingTimingFeature: {
      en: "Floating Timing Feature",
      fa: "ویژگی زمان‌بندی شناور"
    },
    floatingTimingFeatureDesc: {
      en: "The floating timing feature allows for flexible scheduling of content without fixed dates. This is useful for content that needs to be completed within a certain period but doesn't require specific timing.",
      fa: "ویژگی زمان‌بندی شناور امکان برنامه‌ریزی انعطاف‌پذیر محتوا را بدون تاریخ‌های ثابت فراهم می‌کند. این برای محتوایی مفید است که نیاز به تکمیل در یک دوره زمانی خاص دارد اما نیازی به زمان‌بندی مشخص ندارد."
    },
    flexibleScheduling: {
      en: "Flexible Scheduling",
      fa: "برنامه‌ریزی انعطاف‌پذیر"
    },
    flexibleSchedulingDesc: {
      en: "Flexible scheduling options include:\n- Time windows\n- Duration limits\n- Priority levels\n- Deadline ranges\n- Completion targets\nThis allows for more adaptable content management.",
      fa: "گزینه‌های برنامه‌ریزی انعطاف‌پذیر شامل:\n- پنجره‌های زمانی\n- محدودیت‌های مدت زمان\n- سطوح اولویت\n- محدوده مهلت\n- اهداف تکمیل\nاین امکان مدیریت محتوای سازگارتر را فراهم می‌کند."
    },
    deadlineManagement: {
      en: "Deadline Management",
      fa: "مدیریت مهلت"
    },
    deadlineManagementDesc: {
      en: "Deadline management features:\n- Soft deadlines\n- Hard deadlines\n- Extension options\n- Progress tracking\n- Completion notifications\nThese help users manage their time effectively.",
      fa: "ویژگی‌های مدیریت مهلت:\n- مهلت‌های نرم\n- مهلت‌های سخت\n- گزینه‌های تمدید\n- پیگیری پیشرفت\n- اعلان‌های تکمیل\nاین‌ها به کاربران کمک می‌کنند تا زمان خود را به طور مؤثر مدیریت کنند."
    },
    prioritySetting: {
      en: "Priority Setting",
      fa: "تنظیم اولویت"
    },
    prioritySettingDesc: {
      en: "Users can set priorities for floating content:\n- High priority\n- Medium priority\n- Low priority\n- Custom priority levels\nThis helps in organizing and completing tasks efficiently.",
      fa: "کاربران می‌توانند اولویت‌ها را برای محتوای شناور تنظیم کنند:\n- اولویت بالا\n- اولویت متوسط\n- اولویت پایین\n- سطوح اولویت سفارشی\nاین به سازماندهی و تکمیل کارها به طور کارآمد کمک می‌کند."
    },
    staticExpirationTimingFeature: {
      en: "Static Expiration Timing Feature",
      fa: "ویژگی زمان‌بندی انقضا ثابت"
    },
    staticExpirationTimingFeatureDesc: {
      en: "The static expiration timing feature allows content to be automatically archived or hidden after a specific date and time. This is useful for time-sensitive content that should no longer be accessible after a certain point.",
      fa: "ویژگی زمان‌بندی انقضا ثابت امکان بایگانی یا مخفی کردن خودکار محتوا را پس از تاریخ و زمان مشخص فراهم می‌کند. این برای محتوای حساس به زمان مفید است که پس از زمان مشخصی نباید قابل دسترسی باشد."
    },
    expirationSettings: {
      en: "Expiration Settings",
      fa: "تنظیمات انقضا"
    },
    expirationSettingsDesc: {
      en: "Expiration settings include:\n- Expiration date and time\n- Auto-archive option\n- Notification before expiration\n- Grace period settings\n- Extension options\nThese help manage content lifecycle effectively.",
      fa: "تنظیمات انقضا شامل:\n- تاریخ و زمان انقضا\n- گزینه بایگانی خودکار\n- اعلان قبل از انقضا\n- تنظیمات دوره مهلت\n- گزینه‌های تمدید\nاین‌ها به مدیریت مؤثر چرخه حیات محتوا کمک می‌کنند."
    },
    contentLifecycle: {
      en: "Content Lifecycle",
      fa: "چرخه حیات محتوا"
    },
    contentLifecycleDesc: {
      en: "Content lifecycle management:\n- Active period\n- Expiration period\n- Archive period\n- Deletion options\n- Recovery options\nThis ensures proper content management throughout its lifecycle.",
      fa: "مدیریت چرخه حیات محتوا:\n- دوره فعال\n- دوره انقضا\n- دوره بایگانی\n- گزینه‌های حذف\n- گزینه‌های بازیابی\nاین اطمینان حاصل می‌کند که محتوا در طول چرخه حیات خود به درستی مدیریت شود."
    },
    expirationNotifications: {
      en: "Expiration Notifications",
      fa: "اعلان‌های انقضا"
    },
    expirationNotificationsDesc: {
      en: "Users receive notifications about content expiration:\n- Pre-expiration alerts\n- Expiration confirmations\n- Archive notifications\n- Recovery reminders\n- Extension options\nThis helps users stay informed about content status.",
      fa: "کاربران اعلان‌هایی درباره انقضای محتوا دریافت می‌کنند:\n- هشدارهای قبل از انقضا\n- تأییدهای انقضا\n- اعلان‌های بایگانی\n- یادآوری‌های بازیابی\n- گزینه‌های تمدید\nاین به کاربران کمک می‌کند تا از وضعیت محتوا مطلع باشند."
    },
    floatingExpirationTimingFeature: {
      en: "Floating Expiration Timing Feature",
      fa: "ویژگی زمان‌بندی انقضا شناور"
    },
    floatingExpirationTimingFeatureDesc: {
      en: "The floating expiration timing feature allows content to be automatically archived or hidden after a relative time period from when a user joins or accesses the content. This is useful for time-sensitive content that should expire based on user engagement rather than a fixed date.",
      fa: "ویژگی زمان‌بندی انقضا شناور امکان بایگانی یا مخفی کردن خودکار محتوا را پس از یک دوره زمانی نسبی از زمان پیوستن یا دسترسی کاربر به محتوا فراهم می‌کند. این برای محتوای حساس به زمان مفید است که باید بر اساس تعامل کاربر به جای تاریخ ثابت منقضی شود."
    },
    relativeExpiration: {
      en: "Relative Expiration",
      fa: "انقضای نسبی"
    },
    relativeExpirationDesc: {
      en: "Relative expiration settings include:\n- Days from join date\n- Hours from access\n- Custom time periods\n- User-specific deadlines\n- Group-based expiration\nThis allows for personalized content expiration.",
      fa: "تنظیمات انقضای نسبی شامل:\n- روزها از تاریخ پیوستن\n- ساعت‌ها از زمان دسترسی\n- دوره‌های زمانی سفارشی\n- مهلت‌های خاص کاربر\n- انقضا بر اساس گروه\nاین امکان انقضای شخصی‌سازی شده محتوا را فراهم می‌کند."
    },
    userBasedExpiration: {
      en: "User-Based Expiration",
      fa: "انقضا بر اساس کاربر"
    },
    userBasedExpirationDesc: {
      en: "User-based expiration features:\n- Individual timelines\n- Group deadlines\n- Role-based expiration\n- Access-based timing\n- Custom user rules\nThis ensures content remains relevant to each user.",
      fa: "ویژگی‌های انقضا بر اساس کاربر:\n- خط زمانی فردی\n- مهلت‌های گروهی\n- انقضا بر اساس نقش\n- زمان‌بندی بر اساس دسترسی\n- قوانین سفارشی کاربر\nاین اطمینان حاصل می‌کند که محتوا برای هر کاربر مرتبط باقی می‌ماند."
    },
    expirationRules: {
      en: "Expiration Rules",
      fa: "قوانین انقضا"
    },
    expirationRulesDesc: {
      en: "Expiration rules can be set for:\n- Content access\n- User roles\n- Membership levels\n- Time periods\n- Custom conditions\nThese rules help manage content availability effectively.",
      fa: "قوانین انقضا می‌توانند برای موارد زیر تنظیم شوند:\n- دسترسی به محتوا\n- نقش‌های کاربری\n- سطوح عضویت\n- دوره‌های زمانی\n- شرایط سفارشی\nاین قوانین به مدیریت مؤثر دسترسی به محتوا کمک می‌کنند."
    },
    priceFeature: {
      en: "Price Feature",
      fa: "ویژگی قیمت"
    },
    priceFeatureDesc: {
      en: "To sell the content, a price can be entered in 'Iranian Rial'. In contents that allow membership, the user becomes a member of the content after purchase. In non-membership contents, which are usually physical products for sale, after purchase, the user will not become a member of the content, and only a transaction will be recorded for them, which is considered as a receipt for the purchase of the product.",
      fa: "برای فروش محتوا، می‌توان قیمتی به 'ریال ایرانی' وارد کرد. در محتوایی که عضویت را مجاز می‌کند، کاربر پس از خرید به عضو محتوا تبدیل می‌شود. در محتوای غیر عضویت، که معمولاً محصولات فیزیکی برای فروش هستند، پس از خرید، کاربر به عضو محتوا تبدیل نمی‌شود و فقط یک تراکنش برای او ثبت می‌شود که به عنوان رسید خرید محصول محسوب می‌شود."
    },
    pricingOptions: {
      en: "Pricing Options",
      fa: "گزینه‌های قیمت‌گذاری"
    },
    pricingOptionsDesc: {
      en: "Pricing options include:\n- Fixed price\n- Variable pricing\n- Membership pricing\n- Bulk discounts\n- Special offers\nThese help manage content monetization effectively.",
      fa: "گزینه‌های قیمت‌گذاری شامل:\n- قیمت ثابت\n- قیمت‌گذاری متغیر\n- قیمت‌گذاری عضویت\n- تخفیف‌های عمده\n- پیشنهادات ویژه\nاین‌ها به مدیریت مؤثر درآمدزایی از محتوا کمک می‌کنند."
    },
    transactionManagement: {
      en: "Transaction Management",
      fa: "مدیریت تراکنش"
    },
    transactionManagementDesc: {
      en: "Transaction management features:\n- Purchase records\n- Payment processing\n- Receipt generation\n- Refund handling\n- Transaction history\nThis ensures proper tracking of all sales.",
      fa: "ویژگی‌های مدیریت تراکنش:\n- رکوردهای خرید\n- پردازش پرداخت\n- تولید رسید\n- مدیریت بازپرداخت\n- تاریخچه تراکنش\nاین اطمینان حاصل می‌کند که تمام فروش‌ها به درستی پیگیری می‌شوند."
    },
    membershipIntegration: {
      en: "Membership Integration",
      fa: "یکپارچه‌سازی عضویت"
    },
    membershipIntegrationDesc: {
      en: "Membership integration features:\n- Automatic enrollment\n- Access management\n- Duration tracking\n- Renewal options\n- Member benefits\nThis streamlines the purchase-to-access process.",
      fa: "ویژگی‌های یکپارچه‌سازی عضویت:\n- ثبت‌نام خودکار\n- مدیریت دسترسی\n- پیگیری مدت زمان\n- گزینه‌های تمدید\n- مزایای عضویت\nاین فرآیند خرید تا دسترسی را ساده‌تر می‌کند."
    },
    capacityFeature: {
      en: "Capacity Feature",
      fa: "ویژگی ظرفیت"
    },
    capacityFeatureDesc: {
      en: "Determines the number of users who can join the content. This feature helps manage the maximum number of participants for programs, sections, or views.",
      fa: "تعداد کاربرانی که می‌توانند به محتوا بپیوندند را تعیین می‌کند. این ویژگی به مدیریت حداکثر تعداد شرکت‌کنندگان برای برنامه‌ها، بخش‌ها یا نمایش‌ها کمک می‌کند."
    },
    capacityManagement: {
      en: "Capacity Management",
      fa: "مدیریت ظرفیت"
    },
    capacityManagementDesc: {
      en: "Capacity management features:\n- Maximum participant limits\n- Waiting list options\n- Overflow handling\n- Capacity notifications\n- Dynamic adjustments\nThis ensures proper resource allocation.",
      fa: "ویژگی‌های مدیریت ظرفیت:\n- محدودیت‌های حداکثر شرکت‌کننده\n- گزینه‌های لیست انتظار\n- مدیریت سرریز\n- اعلان‌های ظرفیت\n- تنظیمات پویا\nاین اطمینان حاصل می‌کند که منابع به درستی تخصیص داده می‌شوند."
    },
    capacityTypes: {
      en: "Capacity Types",
      fa: "انواع ظرفیت"
    },
    capacityTypesDesc: {
      en: "Different types of capacity settings:\n- Fixed capacity\n- Flexible capacity\n- Unlimited capacity\n- Time-based capacity\n- Role-based capacity\nThese help accommodate various content needs.",
      fa: "انواع مختلف تنظیمات ظرفیت:\n- ظرفیت ثابت\n- ظرفیت انعطاف‌پذیر\n- ظرفیت نامحدود\n- ظرفیت مبتنی بر زمان\n- ظرفیت مبتنی بر نقش\nاین‌ها به پاسخگویی به نیازهای مختلف محتوا کمک می‌کنند."
    },
    capacityNotifications: {
      en: "Capacity Notifications",
      fa: "اعلان‌های ظرفیت"
    },
    capacityNotificationsDesc: {
      en: "Capacity-related notifications:\n- Capacity reached alerts\n- Waiting list updates\n- Spot availability notices\n- Capacity change notifications\n- Join request status\nThis keeps users informed about capacity status.",
      fa: "اعلان‌های مرتبط با ظرفیت:\n- هشدارهای رسیدن به ظرفیت\n- به‌روزرسانی‌های لیست انتظار\n- اطلاع‌رسانی‌های موجودی جای خالی\n- اعلان‌های تغییر ظرفیت\n- وضعیت درخواست پیوستن\nاین کاربران را از وضعیت ظرفیت مطلع نگه می‌دارد."
    },
    dutyFeature: {
      en: "Duty Feature",
      fa: "ویژگی وظیفه"
    },
    dutyFeatureDesc: {
      en: "If this feature is activated for a content, it means that this content is a duty. A duty is a content that the owner or managers assign to others. Duty is a specific type of content that has a status. If the program view is a duty, has a date, and is created with public access permission, all users of the program will join that duty. This protocol also applies to specific contents within program sections for members of those sections.",
      fa: "اگر این ویژگی برای یک محتوا فعال شود، به این معنی است که این محتوا یک وظیفه است. وظیفه محتوایی است که مالک یا مدیران به دیگران محول می‌کنند. وظیفه نوع خاصی از محتوا است که دارای وضعیت است. اگر نمایش برنامه یک وظیفه باشد، دارای تاریخ باشد و با دسترسی عمومی ایجاد شود، تمام کاربران برنامه به آن وظیفه می‌پیوندند. این پروتکل همچنین برای محتوای خاص در بخش‌های برنامه برای اعضای آن بخش‌ها نیز اعمال می‌شود."
    },
    dutyStatus: {
      en: "Duty Status",
      fa: "وضعیت وظیفه"
    },
    dutyStatusDesc: {
      en: "The status in the content indicates the following conditions:\n- Not Done\n- In Progress\n- Done\n- Cancelled\n- On Hold\nThese statuses help track duty progress.",
      fa: "وضعیت در محتوا شرایط زیر را نشان می‌دهد:\n- انجام نشده\n- در حال انجام\n- انجام شده\n- لغو شده\n- در انتظار\nاین وضعیت‌ها به پیگیری پیشرفت وظیفه کمک می‌کنند."
    },
    dutyAssignment: {
      en: "Duty Assignment",
      fa: "تخصیص وظیفه"
    },
    dutyAssignmentDesc: {
      en: "Duty assignment features:\n- Individual assignments\n- Group assignments\n- Role-based assignments\n- Deadline setting\n- Priority levels\nThis ensures proper task distribution.",
      fa: "ویژگی‌های تخصیص وظیفه:\n- تخصیص فردی\n- تخصیص گروهی\n- تخصیص بر اساس نقش\n- تنظیم مهلت\n- سطوح اولویت\nاین اطمینان حاصل می‌کند که وظایف به درستی توزیع می‌شوند."
    },
    dutyTracking: {
      en: "Duty Tracking",
      fa: "پیگیری وظیفه"
    },
    dutyTrackingDesc: {
      en: "Duty tracking features:\n- Progress monitoring\n- Status updates\n- Completion reports\n- Performance metrics\n- Feedback system\nThis helps manage duty completion effectively.",
      fa: "ویژگی‌های پیگیری وظیفه:\n- نظارت بر پیشرفت\n- به‌روزرسانی وضعیت\n- گزارش‌های تکمیل\n- معیارهای عملکرد\n- سیستم بازخورد\nاین به مدیریت مؤثر تکمیل وظیفه کمک می‌کند."
    }
  };

  const getTranslation = (key) => {
    const lang = i18n.language.toLowerCase();
    return translations[key]?.[lang] || translations[key]?.en || key;
  };

  const getCapabilitiesTranslation = (key, index) => {
    const translation = translations[key];
    if (!translation) return '';
    return translation[i18n.language.toLowerCase()]?.[index] || translation.en[index] || '';
  };

  return (
    <div className='guide-container' dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={theme === 'light' ? 'light-guide-list-wrapper' : 'guide-list-wrapper'}>
        <h2 className={theme === 'light' ? 'light-guide-title' : 'guide-title'}>
          {i18n.language.toLowerCase() === 'fa' ? 'تعاریف' : 'Definitions'}
          <ArrowDropDownOutlinedIcon sx={{ marginBottom: '-5px' }} />
        </h2>
        {guideList1.map(item =>
          <div key={item.id}>
            <a className={theme === 'light' ? 'light-guide-item' : 'guide-item'} href={item.href}>
              {getTitle(item)}
            </a>
          </div>
        )}
        <h2 className={theme === 'light' ? 'light-guide-title' : 'guide-title'}>
          {i18n.language.toLowerCase() === 'fa' ? 'امکانات' : 'Features'}
          <ArrowDropDownOutlinedIcon sx={{ marginBottom: '-5px' }} />
        </h2>
        {guideList2.map(item =>
          <div key={item.id}>
            <a className={theme === 'light' ? 'light-guide-item' : 'guide-item'} href={item.href}>
              {getTitle(item)}
            </a>
          </div>
        )}
      </div>
      <DropDownGuideItems />
      <div className={theme === 'light' ? "light-guide-content-wrapper" : 'guide-content-wrapper'}>
        <h1 className={theme === 'light' ? 'light-h1-guide-content' : 'h1-guide-contnet'}>
          {i18n.language.toLowerCase() === 'fa' ? 'تعاریف' : 'Definitions'}
        </h1>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='normal-user'>{getTranslation('user')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('userDesc')}<br />
          {getTranslation('userCapabilities')}<br />
          {translations.capabilities[i18n.language.toLowerCase()].map((capability, index) => (
            <React.Fragment key={index}>
              {capability}<br />
            </React.Fragment>
          ))}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='owner'>{getTranslation('owner')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('ownerDesc')}<br />
          {translations.ownerCapabilities[i18n.language.toLowerCase()].map((capability, index) => (
            <React.Fragment key={index}>
              {capability}<br />
            </React.Fragment>
          ))}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='manager'>{getTranslation('manager')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('managerDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='plan'>{getTranslation('program')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('programDesc')}<br />
          {getTranslation('programViews')}<br />
          {getTranslation('programJoin')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='section'>{getTranslation('section')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('sectionDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='view'>{getTranslation('view')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('viewDesc')}<br />
          {getTranslation('personalView')}<br />
          {getTranslation('viewNote')}<br />
          {getTranslation('programView')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='content'>{getTranslation('content')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('contentDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='notification'>{getTranslation('notification')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('notificationDesc')}<br />
          {getTranslation('notificationSettings')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='access'>{getTranslation('accessPermission')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('accessPermissionDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='access-to-personal-views'>{getTranslation('accessToPersonalViews')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('accessToPersonalViewsDesc1')}<br />
          {getTranslation('accessToPersonalViewsDesc2')}<br />
          {getTranslation('accessToPersonalViewsDesc3')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='access-to-program-contents'>{getTranslation('accessToProgramContents')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('accessToProgramContentsDesc1')}<br />
          {getTranslation('accessToProgramContentsDesc2')}<br />
          {getTranslation('accessToProgramContentsDesc3')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='membership'>{getTranslation('membership')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('membershipDesc')}
        </p>
        <h5 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('membershipCapabilities')}</h5>
        <ul className={theme === 'light' ? 'light-guide-list' : 'guide-list'}>
          {translations.membershipCapabilities[i18n.language.toLowerCase()].map((capability, index) => (
            <li key={index} className={theme === 'light' ? 'light-guide-list-item' : 'guide-list-item'}>
              {capability}
            </li>
          ))}
        </ul>
        <h5 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('membershipTypes')}</h5>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('membershipTypesDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('freeMembership')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('freeMembershipDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('paidMembership')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('paidMembershipDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('premiumMembership')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('premiumMembershipDesc')}
        </p>
        <h5 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('membershipBenefits')}</h5>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('membershipBenefitsDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('contentAccess')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('contentAccessDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('communityInteraction')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('communityInteractionDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('notifications')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('notificationsDesc')}
        </p>
        <h5 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('membershipRestrictions')}</h5>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('membershipRestrictionsDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('contentLimitations')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('contentLimitationsDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('timeRestrictions')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('timeRestrictionsDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('usageRestrictions')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('usageRestrictionsDesc')}
        </p>
        <h5 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('membershipManagement')}</h5>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('membershipManagementDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('viewMemberships')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('viewMembershipsDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('cancelMembership')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('cancelMembershipDesc')}
        </p>
        <h5 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('membershipRenewal')}</h5>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('membershipRenewalDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('automaticRenewal')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('automaticRenewalDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('manualRenewal')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('manualRenewalDesc')}
        </p>
        <h6 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>{getTranslation('renewalReminders')}</h6>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('renewalRemindersDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='exit'>{getTranslation('exit')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('exitDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='add-icon-section1'>{getTranslation('addIcon')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('addIconDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='create-content-section1'>{getTranslation('createContent')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('createContentDesc')}
        </p>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('createContentNote')}
        </p>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('createContentImportant')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='program-feature-section1'>{getTranslation('programFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('programFeatureDesc')}
        </p>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('programFeatureNote')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='program-access-section1'>{getTranslation('programAccess')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('programAccessDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='program-membership-section1'>{getTranslation('programMembership')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('programMembershipDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='section-feature-section1'>{getTranslation('sectionFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('sectionFeatureDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='section-access-section1'>{getTranslation('sectionAccess')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('sectionAccessDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='section-membership-section1'>{getTranslation('sectionMembership')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('sectionMembershipDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='section-content-section1'>{getTranslation('sectionContent')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('sectionContentDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='title-feature-section1'>{getTranslation('titleFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('titleFeatureDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='title-rules-section1'>{getTranslation('titleRules')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('titleRulesDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='title-search-section1'>{getTranslation('titleSearch')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('titleSearchDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='description-feature-section1'>{getTranslation('descriptionFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('descriptionFeatureDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='description-format-section1'>{getTranslation('descriptionFormat')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('descriptionFormatDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='description-search-section1'>{getTranslation('descriptionSearch')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('descriptionSearchDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='media-feature-section1'>{getTranslation('mediaFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('mediaFeatureDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='media-types-section1'>{getTranslation('mediaTypes')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('mediaTypesDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='media-upload-section1'>{getTranslation('mediaUpload')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('mediaUploadDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='media-display-section1'>{getTranslation('mediaDisplay')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('mediaDisplayDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='access-permission-feature-section1'>{getTranslation('accessPermissionFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('accessPermissionFeatureDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='public-access-section1'>{getTranslation('publicAccess')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('publicAccessDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='protected-access-section1'>{getTranslation('protectedAccess')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('protectedAccessDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='private-access-section1'>{getTranslation('privateAccess')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('privateAccessDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='access-management-section1'>{getTranslation('accessManagement')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('accessManagementDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='static-timing-feature-section1'>{getTranslation('staticTimingFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('staticTimingFeatureDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='date-setting-section1'>{getTranslation('dateSetting')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('dateSettingDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='time-management-section1'>{getTranslation('timeManagement')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('timeManagementDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='scheduling-options-section1'>{getTranslation('schedulingOptions')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('schedulingOptionsDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='floating-timing-feature-section1'>{getTranslation('floatingTimingFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('floatingTimingFeatureDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='flexible-scheduling-section1'>{getTranslation('flexibleScheduling')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('flexibleSchedulingDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='deadline-management-section1'>{getTranslation('deadlineManagement')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('deadlineManagementDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='priority-setting-section1'>{getTranslation('prioritySetting')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('prioritySettingDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='static-expiration-timing-feature-section1'>{getTranslation('staticExpirationTimingFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('staticExpirationTimingFeatureDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='price-feature-section1'>{getTranslation('priceFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('priceFeatureDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='pricing-options-section1'>{getTranslation('pricingOptions')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('pricingOptionsDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='transaction-management-section1'>{getTranslation('transactionManagement')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('transactionManagementDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='membership-integration-section1'>{getTranslation('membershipIntegration')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('membershipIntegrationDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='capacity-feature-section1'>{getTranslation('capacityFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('capacityFeatureDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='duty-feature-section1'>{getTranslation('dutyFeature')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('dutyFeatureDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='duty-status-section1'>{getTranslation('dutyStatus')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('dutyStatusDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='duty-assignment-section1'>{getTranslation('dutyAssignment')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('dutyAssignmentDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='duty-tracking-section1'>{getTranslation('dutyTracking')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('dutyTrackingDesc')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='access-to-program-contents-section1'>{getTranslation('accessToProgramContents')}</h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('accessToProgramContentsDesc1')}
        </p>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('accessToProgramContentsDesc2')}
        </p>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('accessToProgramContentsDesc3')}
        </p>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='user-capabilities-section'>{getTranslation('userCapabilities')}</h4>
        <ul className={theme === 'light' ? 'light-guide-list' : 'guide-list'}>
          {translations.capabilities[i18n.language.toLowerCase()].map((capability, index) => (
            <li key={index} className={theme === 'light' ? 'light-guide-list-item' : 'guide-list-item'}>
              {capability}
            </li>
          ))}
        </ul>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='owner-capabilities-section'>{getTranslation('ownerCapabilities')}</h4>
        <ul className={theme === 'light' ? 'light-guide-list' : 'guide-list'}>
          {translations.ownerCapabilities[i18n.language.toLowerCase()].map((capability, index) => (
            <li key={index} className={theme === 'light' ? 'light-guide-list-item' : 'guide-list-item'}>
              {capability}
            </li>
          ))}
        </ul>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'} id='membership-capabilities-section'>{getTranslation('membershipCapabilities')}</h4>
        <ul className={theme === 'light' ? 'light-guide-list' : 'guide-list'}>
          {translations.membershipCapabilities[i18n.language.toLowerCase()].map((capability, index) => (
            <li key={index} className={theme === 'light' ? 'light-guide-list-item' : 'guide-list-item'}>
              {capability}
            </li>
          ))}
        </ul>
        <h4 className={theme === 'light' ? 'light-guide-content-title' : 'guide-content-title'}>
          {getTranslation('membershipTitle')}
        </h4>
        <p className={theme === 'light' ? 'light-guide-para' : 'guide-para'}>
          {getTranslation('membershipDesc')}
        </p>
        <h5 className={theme === 'light' ? 'light-guide-content-subtitle' : 'guide-content-subtitle'}>
          {getTranslation('membershipCapabilitiesTitle')}
        </h5>
        <ul className={theme === 'light' ? 'light-guide-list' : 'guide-list'}>
          {translations.membershipCapabilities[i18n.language.toLowerCase()].map((capability, index) => (
            <li key={index} className={theme === 'light' ? 'light-guide-list-item' : 'guide-list-item'}>
              {capability}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
